import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Globe, Facebook, Instagram, Linkedin, Youtube, Cookie } from 'lucide-react';
import { useConsent } from '../contexts/ConsentContext';

const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
];

// Social media links - Update these with your actual profile URLs
const socialLinks = [
  { name: 'YouTube', icon: Youtube, url: 'https://www.youtube.com/@Tradehub247', color: 'hover:text-red-500' },
  { name: 'LinkedIn', icon: Linkedin, url: 'https://www.linkedin.com/company/tradehub24', color: 'hover:text-blue-400' },
  { name: 'Facebook', icon: Facebook, url: 'https://www.facebook.com/profile.php?id=61582194794357#', color: 'hover:text-blue-500' },
  { name: 'Instagram', icon: Instagram, url: 'https://www.instagram.com/tradehub247365/', color: 'hover:text-pink-500'  },
];

const Footer = () => {
  const { t, i18n } = useTranslation();
  const { openPreferences } = useConsent();
  const currentYear = new Date().getFullYear();

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    // Also trigger SEO update
    if (window.updateSEOMetaTags) {
      setTimeout(() => window.updateSEOMetaTags(langCode), 100);
    }
  };

  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

  return (
    <footer className="bg-slate-900 text-slate-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center space-x-2 mb-4">
              <img src="/tradehub_logo.png" alt="TradeHub" className="h-10" />
            </div>
            <p className="text-sm text-slate-400 mb-4 max-w-md">
              {t('footer.description')}
            </p>
            
            {/* Social Media Icons */}
            <div className="flex items-center gap-3">
              <span className="text-sm text-slate-500 mr-2">{t('footer.follow_us') || 'Follow us'}:</span>
              {socialLinks.filter(s => !s.disabled).map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`p-2 rounded-full bg-slate-800 text-slate-400 transition-all duration-200 ${social.color} hover:bg-slate-700 hover:scale-110`}
                  aria-label={social.name}
                  title={social.name}
                >
                  <social.icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold mb-4">{t('footer.quick_links')}</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/" className="hover:text-white transition-colors">
                  {t('nav.home')}
                </Link>
              </li>
              <li>
                <Link to="/events" className="hover:text-white transition-colors">
                  {t('nav.events')}
                </Link>
              </li>
              <li>
                <Link to="/about" className="hover:text-white transition-colors">
                  {t('nav.about')}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-white transition-colors">
                  {t('nav.contact')}
                </Link>
              </li>
              <li>
                <Link to="/knowledge" className="hover:text-white transition-colors">
                  {t('knowledge.title')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Language Selector */}
          <div>
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Globe className="h-4 w-4" />
              {t('footer.language')}
            </h3>
            <div className="space-y-2">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className={`flex items-center gap-2 w-full text-left px-3 py-2 rounded text-sm transition-colors ${
                    i18n.language === lang.code
                      ? 'bg-primary text-white'
                      : 'hover:bg-slate-800 text-slate-300 hover:text-white'
                  }`}
                >
                  <span className="text-lg">{lang.flag}</span>
                  <span>{lang.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-slate-700 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            {/* Copyright */}
            <div className="text-sm text-slate-400">
              © {currentYear} TradeHub24, Inc. {t('footer.rights')}.
            </div>

            {/* Legal Links */}
            <div className="flex items-center gap-4 text-sm flex-wrap">
              <Link to="/privacy-policy" className="hover:text-white transition-colors">
                {t('footer.privacy')}
              </Link>
              <Link to="/terms-of-service" className="hover:text-white transition-colors">
                {t('footer.terms')}
              </Link>
              <button 
                onClick={openPreferences}
                className="hover:text-white transition-colors flex items-center gap-1"
              >
                <Cookie className="h-3 w-3" />
                {t('footer.cookies', 'Manage Cookies')}
              </button>
              <Link to="/sitemap" className="hover:text-white transition-colors">
                {t('footer.sitemap')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
